"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@strivelabs/ui-core";
import { Button, IconButton } from "@strivelabs/ui-core/v2";
import { Check, Copy, Download, Maximize2, Minus, Plus } from "lucide-react";
import { useCopyToClipboard } from "../../hooks/useCopyToClipboard";
import {
  buildMermaidThemeCss,
  buildMermaidThemeVariables,
  MERMAID_TOKEN_FALLBACKS,
  MERMAID_TOKEN_SOURCES,
  isDarkSurface,
  mermaidDiagramKind,
  type MermaidTokenName,
  recolorJourneyActors,
  sanitizeMermaidSource,
  toOpaqueHex,
  withFixedSize,
  withSemanticClasses,
} from "./mermaid-utils";

type MermaidApi = typeof import("mermaid").default;

let mermaidPromise: Promise<MermaidApi> | null = null;
/**
 * Mermaid is ~1MB; load it only when a diagram actually mounts. A failed
 * chunk load (network blip, stale chunk after a deploy) is not cached, so the
 * next diagram retries instead of every diagram failing until a reload.
 */
const loadMermaid = () => {
  mermaidPromise ??= import("mermaid").then(
    (module) => module.default,
    (error: unknown) => {
      mermaidPromise = null;
      throw error;
    }
  );
  return mermaidPromise;
};

// Mermaid's config is a process-wide singleton (Streamdown's own fence
// renderer initialises it too), so each initialise + render pair runs
// serially — two diagrams rendering at once must not swap themes mid-render.
let renderQueue: Promise<unknown> = Promise.resolve();
const serialize = <T,>(task: () => Promise<T>): Promise<T> => {
  const run = renderQueue.then(task, task);
  renderQueue = run.catch(() => undefined);
  return run;
};

let renderCount = 0;

/**
 * Resolve the chat's CSS tokens to opaque hex values by letting the browser
 * compute each expression on a probe element inside `host`, so the diagram
 * inherits whatever theme (light, dark, tenant brand) that surface shows.
 */
function resolveTokens(host: HTMLElement): Record<MermaidTokenName, string> {
  const probe = document.createElement("span");
  probe.style.display = "none";
  host.appendChild(probe);
  const read = (expression: string) => {
    probe.style.color = "";
    probe.style.color = expression;
    return probe.style.color ? getComputedStyle(probe).color : "";
  };
  const surface = toOpaqueHex(read(MERMAID_TOKEN_SOURCES.surface)) ?? MERMAID_TOKEN_FALLBACKS.surface;
  const tokens = { surface } as Record<MermaidTokenName, string>;
  for (const name of Object.keys(MERMAID_TOKEN_SOURCES) as MermaidTokenName[]) {
    if (name === "surface") continue;
    tokens[name] = toOpaqueHex(read(MERMAID_TOKEN_SOURCES[name]), surface) ?? MERMAID_TOKEN_FALLBACKS[name];
  }
  probe.remove();
  return tokens;
}

/** Every CSS custom property the theme reads, e.g. `--custom-card-bg`. */
const THEME_VARIABLES = Array.from(
  new Set(Object.values(MERMAID_TOKEN_SOURCES).flatMap((expression) => expression.match(/--[\w-]+/g) ?? []))
);

/** The resolved values of the theme's variables on <html> — changes only when the theme does. */
function themeSignature(): string {
  const style = getComputedStyle(document.documentElement);
  return THEME_VARIABLES.map((name) => style.getPropertyValue(name).trim()).join("|");
}

/**
 * Re-render when the app theme flips (class / data-theme / inline token
 * changes on <html>). Attribute writes that leave the tokens untouched — an
 * unrelated inline style, a class toggled for something else — are ignored,
 * so a long thread's diagrams don't all re-render for nothing.
 */
function useThemeVersion(): number {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    let signature = themeSignature();
    const observer = new MutationObserver(() => {
      const next = themeSignature();
      if (next === signature) return;
      signature = next;
      setVersion((value) => value + 1);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "data-theme", "style"],
    });
    return () => observer.disconnect();
  }, []);
  return version;
}

export type MermaidDiagramAction = "expand" | "download" | "copy";

type RenderState =
  | { status: "loading" }
  | { status: "ready"; svg: string }
  | { status: "invalid"; error: Error };

export interface MermaidDiagramProps {
  /** Mermaid source. Styling directives are stripped before rendering. */
  source: string;
  /** Heading shown above the diagram. */
  title?: string;
  /** One-line takeaway shown under the diagram. */
  caption?: string;
  /**
   * What to show when the source does not parse. `hide` renders nothing —
   * right for the `ui_mermaid_diagram` tool, whose server gate already made
   * the model retry, so the failed attempt should leave no trace. `source`
   * falls back to the raw text as a code block — right for a markdown fence,
   * where the text is the only copy of what the model wrote.
   * @default "hide"
   */
  onInvalid?: "hide" | "source";
  /** Called once per source that fails to parse or render. */
  onRenderError?: (error: Error) => void;
  /** Called when the reader uses a toolbar action — for usage analytics. */
  onAction?: (action: MermaidDiagramAction) => void;
  /** Show the expand / download / copy toolbar. @default true */
  showActions?: boolean;
  /** Card background. Defaults to the chat card surface token. */
  backgroundColor?: string;
  className?: string;
}

/**
 * Renders a mermaid diagram themed from the surrounding chat tokens.
 *
 * Parses before rendering (mermaid's own error path injects a bomb graphic
 * into `document.body`), keeps mermaid's default HTML labels — mermaid 11's
 * unified renderers (mindmap, ER relationship labels) render blank with
 * `htmlLabels: false`, and Chromium, WebKit and Firefox all rasterise the
 * labels into the PNG export — and re-themes on light/dark switches.
 */
export function MermaidDiagram({
  source,
  title,
  caption,
  onInvalid = "hide",
  onRenderError,
  onAction,
  showActions = true,
  backgroundColor,
  className,
}: MermaidDiagramProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<RenderState>({ status: "loading" });
  const [expanded, setExpanded] = useState(false);
  const [zoom, setZoom] = useState(1);
  const themeVersion = useThemeVersion();
  const { copied, copy } = useCopyToClipboard();
  const onRenderErrorRef = useRef(onRenderError);
  onRenderErrorRef.current = onRenderError;

  useEffect(() => {
    let cancelled = false;
    const host = hostRef.current;
    if (!host) return;
    const cleanSource = sanitizeMermaidSource(source);

    serialize(async () => {
      const mermaid = await loadMermaid();
      const tokens = resolveTokens(host);
      const fontFamily = getComputedStyle(host).fontFamily || "Inter, sans-serif";
      const dark = isDarkSurface(tokens.surface);
      const kind = mermaidDiagramKind(cleanSource);
      const renderSource = withSemanticClasses(cleanSource, kind, tokens, dark);
      mermaid.initialize({
        startOnLoad: false,
        securityLevel: "strict",
        theme: "base",
        htmlLabels: true,
        flowchart: { htmlLabels: true, curve: "basis", padding: 14, nodeSpacing: 36, rankSpacing: 44 },
        sequence: { useMaxWidth: true, actorMargin: 56, boxMargin: 8, noteMargin: 10, messageMargin: 36, mirrorActors: false },
        state: { padding: 10 },
        themeCSS: buildMermaidThemeCss(tokens, dark),
        // Journey styles its title from its own config, not the theme; its
        // actor colours cannot be configured at all (see recolorJourneyActors).
        journey: { titleFontFamily: fontFamily, titleColor: tokens.text },
        themeVariables: buildMermaidThemeVariables(tokens, dark, fontFamily),
      });
      await mermaid.parse(renderSource);
      const id = `strive-mermaid-${++renderCount}`;
      try {
        const { svg } = await mermaid.render(id, renderSource);
        return kind === "journey"
          ? recolorJourneyActors(svg, [tokens.accent1, tokens.accent2, tokens.accent3, tokens.accent4, tokens.accent5])
          : svg;
      } finally {
        document.getElementById(`d${id}`)?.remove();
        document.getElementById(id)?.remove();
      }
    })
      .then((svg) => {
        if (!cancelled) setState({ status: "ready", svg });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const err = error instanceof Error ? error : new Error(String(error));
        setState({ status: "invalid", error: err });
        onRenderErrorRef.current?.(err);
      });

    return () => {
      cancelled = true;
    };
  }, [source, themeVersion]);

  const downloadPng = useCallback(() => {
    if (state.status !== "ready") return;
    const svgElement = hostRef.current?.querySelector("svg");
    const box = svgElement?.getBoundingClientRect();
    const width = Math.max(1, Math.round(box?.width ?? 800));
    const height = Math.max(1, Math.round(box?.height ?? 600));
    const scale = 2;
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width * scale;
      canvas.height = height * scale;
      const context = canvas.getContext("2d");
      if (!context) return;
      context.fillStyle = resolveTokens(hostRef.current ?? document.body).surface;
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      const link = document.createElement("a");
      link.download = `${(title || "diagram").replace(/[^\w-]+/g, "-").toLowerCase()}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    };
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(withFixedSize(state.svg, width, height))}`;
  }, [state, title]);

  if (state.status === "invalid") {
    if (onInvalid === "hide") return null;
    return (
      <pre
        className={cn(
          "my-2 overflow-x-auto rounded-lg border border-custom-border bg-custom-bg-primary p-3 font-mono text-xs text-custom-text-secondary",
          className
        )}
        data-testid="mermaid-diagram-source-fallback"
      >
        {source}
      </pre>
    );
  }

  const svgMarkup = state.status === "ready" ? { __html: state.svg } : undefined;

  return (
    <figure
      className={cn(
        "group relative my-2 flex flex-col gap-3 rounded-lg border border-custom-border bg-custom-card-bg p-4",
        className
      )}
      style={backgroundColor ? { backgroundColor } : undefined}
      data-testid="mermaid-diagram"
      data-state={state.status}
    >
      {(title || (showActions && state.status === "ready")) && (
        <div className="flex items-start justify-between gap-3">
          {title ? (
            <h3 className="text-sm font-semibold leading-tight tracking-tight text-custom-text-secondary">
              {title}
            </h3>
          ) : (
            <span />
          )}
          {showActions && state.status === "ready" && (
            <div className="flex shrink-0 items-center gap-1 opacity-70 transition-opacity group-hover:opacity-100">
              <ToolbarButton
                label="Expand diagram"
                onClick={() => {
                  setExpanded(true);
                  onAction?.("expand");
                }}
              >
                <Maximize2 className="size-3.5" />
              </ToolbarButton>
              <ToolbarButton
                label="Download as PNG"
                onClick={() => {
                  downloadPng();
                  onAction?.("download");
                }}
              >
                <Download className="size-3.5" />
              </ToolbarButton>
              <ToolbarButton
                label={copied ? "Copied" : "Copy diagram source"}
                onClick={() => {
                  copy(source);
                  onAction?.("copy");
                }}
              >
                {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
              </ToolbarButton>
            </div>
          )}
        </div>
      )}

      <div ref={hostRef} className="relative w-full">
        {state.status === "loading" ? (
          <div
            className="h-40 w-full animate-pulse rounded-md bg-custom-bg-primary"
            aria-label="Drawing diagram"
            role="status"
          />
        ) : (
          <div
            className="flex w-full justify-center overflow-x-auto [&_svg]:h-auto [&_svg]:max-w-full"
            role="img"
            aria-label={title ?? "Diagram"}
            dangerouslySetInnerHTML={svgMarkup}
          />
        )}
      </div>

      {caption && (
        <figcaption className="text-xs text-custom-text-secondary">{caption}</figcaption>
      )}

      {showActions && state.status === "ready" && (
        <Dialog
          open={expanded}
          onOpenChange={(open) => {
            setExpanded(open);
            if (!open) setZoom(1);
          }}
        >
          <DialogContent className="flex max-h-[90vh] w-[95vw] max-w-7xl flex-col">
            <DialogHeader>
              <DialogTitle>{title ?? "Diagram"}</DialogTitle>
              {caption && <DialogDescription>{caption}</DialogDescription>}
            </DialogHeader>
            <div className="flex items-center justify-end gap-1">
              <ToolbarButton label="Zoom out" onClick={() => setZoom((value) => Math.max(0.5, value - 0.25))}>
                <Minus className="size-3.5" />
              </ToolbarButton>
              <Button
                variant="borderless"
                size="xs"
                onClick={() => setZoom(1)}
                className="min-w-12 tabular-nums"
                aria-label="Reset zoom"
              >
                {Math.round(zoom * 100)}%
              </Button>
              <ToolbarButton label="Zoom in" onClick={() => setZoom((value) => Math.min(3, value + 0.25))}>
                <Plus className="size-3.5" />
              </ToolbarButton>
            </div>
            <div className="min-h-0 flex-1 overflow-auto rounded-md border border-custom-border bg-custom-card-bg p-4">
              <div
                className="mx-auto w-full origin-top [&_svg]:h-auto [&_svg]:!max-w-none [&_svg]:w-full"
                style={{ width: `${zoom * 100}%` }}
                dangerouslySetInnerHTML={svgMarkup}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </figure>
  );
}

function ToolbarButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <IconButton
      size="2xs"
      onClick={onClick}
      aria-label={label}
      title={label}
    >
      {children}
    </IconButton>
  );
}
